import type { Severity } from "./priority";

/**
 * Shared presentational tokens for the worklist surface (rows + the Customer-360
 * slide-over). Extracted so the panel and the row it opened from CANNOT drift apart:
 * the pill a CM clicked must be the same pill they see in the panel header.
 *
 * NOTE ON COLOURS (hard-won — see WorklistCard): this theme (Horizon) does NOT ship a
 * `slate` scale — every `slate-*` class silently no-ops — and it defines `red-50` as
 * `#ee5d501a`, a 10%-ALPHA red rather than a pale tint. Only the clinic/crit/warn/
 * action tokens in tailwind.config.js are real opaque hexes. Colour = meaning:
 * crit/warn carry SEVERITY, action-* is the ONE interactive accent, so a button can
 * never be mistaken for an alarm.
 */

export const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-navy-800";

export const btnBase = `touch-manipulation rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;

export const primaryBtn = `${btnBase} bg-action-600 text-white hover:bg-action-700`;
export const ghostBtn = `${btnBase} bg-white font-medium text-clinic-600 ring-1 ring-clinic-300 hover:bg-clinic-100 hover:text-clinic-900 dark:bg-transparent dark:text-gray-300 dark:ring-navy-600 dark:hover:bg-navy-700`;
export const dangerBtn = `${btnBase} bg-crit-600 text-white hover:bg-crit-700`;

/** Solid, not tinted — the one thing that must survive a 200ms glance. */
export const pillTone: Record<Severity, string> = {
  critical: "bg-crit-600 text-white",
  warning: "bg-warn-100 text-warn-700 ring-1 ring-warn-200",
  muted: "bg-clinic-100 text-clinic-600 ring-1 ring-clinic-200",
};

/** Mono uppercase tracked micro-label — section headers and field labels. */
export const microLabel =
  "font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-clinic-500 dark:text-gray-400";
